import {
  MailIcon,
  BellIcon,
  MegaphoneIcon,
  ChartAreaIcon,
  ChartLineIcon,
  LayoutGridIcon,
  ContactIcon,
  GlobeIcon,
  HomeIcon,
  LightbulbIcon,
  LinkIcon,
  LockIcon,
  PaintbrushIcon,
  PlugIcon,
  SettingsIcon,
  TargetIcon,
  UploadIcon,
  UserPlusIcon,
  UsersIcon,
  WarehouseIcon,
  ZapIcon,
} from "@lucide/icons";
import type { LucideIconData } from "@lucide/icons";

import util from "utils/utils";

import type { GetPathUser } from "./types";

type SpotlightLocation = {
  title: string;
  keywords?: string[];
  location: string;
  icon?: LucideIconData;
  permissionNeeded?: string;
  pages?: SpotlightLocation[];
};

const spotlightLocations: SpotlightLocation[] = [
  {
    title: "Home",
    keywords: ["feed", "dashboard", "activity"],
    location: "",
    icon: HomeIcon,
    pages: [
      {
        title: "Admin",
        permissionNeeded: "org.viewDashboard",
        keywords: ["organisation"],
        location: "admin",
        icon: WarehouseIcon,
        pages: [
          {
            title: "Activity",
            keywords: ["activity", "stats"],
            location: "activity",
            icon: ChartAreaIcon,
            pages: [
              {
                title: "Dashboards",
                keywords: ["stats"],
                location: "dashboards",
                icon: ChartLineIcon,
              },
            ],
          },
          {
            title: "Users",
            keywords: ["people"],
            location: "users",
            icon: UsersIcon,
          },
          { title: "Challenges", location: "challenges", icon: TargetIcon },
          {
            title: "Communication",
            location: "communication",
            icon: MegaphoneIcon,
            pages: [
              {
                title: "Announcements",
                keywords: ["announcements"],
                location: "announcements",
                icon: MegaphoneIcon,
              },
            ],
          },
          {
            title: "Uploads",
            keywords: ["file"],
            location: "uploads",
            icon: UploadIcon,
          },
          {
            title: "Polls",
            location: "polls",
            icon: ZapIcon,
            permissionNeeded: "org.managePolls",
          },
          {
            title: "Links",
            keywords: ["domain"],
            location: "links",
            icon: LinkIcon,
          },
          {
            title: "Settings",
            keywords: [
              "preferences",
              "name",
              "language",
              "permissions",
              "onboarding",
            ],
            location: "settings",
            icon: SettingsIcon,
            pages: [
              {
                title: "Branding",
                keywords: ["colour", "logo", "text", "icon"],
                location: "branding",
                icon: PaintbrushIcon,
              },
              {
                title: "Domains",
                keywords: ["mail", "site"],
                location: "domains",
                icon: GlobeIcon,
              },
              {
                title: "Rules",
                keywords: ["automation"],
                location: "rules",
                icon: LayoutGridIcon,
              },
              {
                title: "Integrations",
                keywords: [
                  "power",
                  "power bi",
                  "creditsafe",
                  "microsoft",
                  "webhook",
                ],
                location: "integrations",
                icon: LayoutGridIcon,
              },
              {
                title: "Sign-on",
                keywords: [
                  "login",
                  "register",
                  "sign-up",
                  "signon",
                  "auth",
                  "sso",
                  "oauth",
                  "saml",
                ],
                location: "signon",
                icon: UserPlusIcon,
              },
            ],
          },
        ],
      },
      {
        title: "Account",
        keywords: ["preferences", "settings", "log", "language"],
        location: "preferences",
        icon: SettingsIcon,
        pages: [
          {
            title: "Profile",
            keywords: [
              "name",
              "avatar",
              "cover",
              "video",
              "overview",
              "tagline",
              "social",
              "website",
              "tags",
              "uploads",
            ],
            location: "profile",
            icon: ContactIcon,
          },
          { title: "Email addresss", location: "email", icon: MailIcon },
          {
            title: "Password",
            keywords: ["multi", "mfa"],
            location: "password",
            icon: LockIcon,
          },
          {
            title: "Notifications",
            keywords: ["notify", "email", "push"],
            location: "notifications",
            icon: BellIcon,
          },
          {
            title: "Connected Services",
            keywords: ["api"],
            location: "connections",
            icon: PlugIcon,
          },
        ],
      },
      {
        title: "Challenges",
        keywords: ["ideas"],
        location: "challenges",
        icon: TargetIcon,
      },
      {
        title: "Ideas",
        keywords: ["challenges"],
        location: "ideas",
        icon: LightbulbIcon,
      },
    ],
  },
];

export type SpotlightResult = {
  title: string;
  link: string;
  icon?: LucideIconData;
};

// Recursive function to build link for spotlight search
const getPath = (
  user: GetPathUser,
  query: string,
  link = "",
  path: SpotlightLocation[] = spotlightLocations,
  results: SpotlightResult[] = [],
): SpotlightResult[] => {
  for (let i = 0; i < path.length; i += 1) {
    if (results.length > 2) break;
    const subPath = path[i];
    if (!subPath) continue;
    const {
      title,
      location: subLocation,
      pages = [],
      icon = "",
      keywords = [],
      permissionNeeded,
    } = subPath;
    if (
      !permissionNeeded ||
      (permissionNeeded &&
        user &&
        util.hasPermission(
          user,
          permissionNeeded,
          user.ownerOrganisation?._id ?? "",
        ))
    ) {
      const updatedLink = link ? `${link}/${subLocation}` : subLocation;
      const titleLower = title.toLowerCase().trim();
      const queryLower = query.toLowerCase().trim();
      const queryInKeywords = keywords.find(
        (k: string) => k.indexOf(queryLower) > -1,
      );
      if (queryInKeywords || titleLower.indexOf(queryLower) > -1) {
        results.push({ title, link: updatedLink, icon: icon || undefined });
      }
      getPath(user, query, updatedLink, pages, results);
    }
  }
  return results;
};

export { getPath, spotlightLocations as locations };

export default spotlightLocations;
